import { ReactNode } from "react";
import { Product } from "~/common/Store/models/ProductListing";
import PageCreatorLandingPage from "..";
import PageCreatorItemListingPage from "../ItemListingPage";
import PageCreatorInactivePage from "../InactivePage";
import PageCreatorLeadGenerationPage from "../LeadGenerationPage";
import { Video } from "~/common/Store/models/Video";

export {
  PageCreatorLandingPage,
  PageCreatorItemListingPage,
  PageCreatorInactivePage,
  PageCreatorLeadGenerationPage,
};

export interface PageCreatorModel {
  pageId: number;
  pageName: string;
  activeStartDate: string;
  activeEndDate: string;
  groups: PageCreatorGroupModel[];
  couponCode: string;
}

export interface PageCreatorGroupModel {
  name: string;
  sortOrder: number;
  sections: PageCreatorSectionModel[];
}

export interface PageCreatorSectionModel {
  sectionId: number;
  sectionStyle: LeadGenStyle;
  description: string;
  sortOrder: number;
  sectionType: PageCreatorSectionType;
  eventHero: EventHeroModel;
  wideImages: WideImageModel[];
  imageLeftCarousel: ImageLeftCarouselModel;
  navBar: NavBarModel;
  cardCarousel: CardCarouselModel;
  standardCarousel: StandardCarouselModel;
  hero: HeroModel;
  content: ContentModel;
  feature: FeatureModel;
  form: FormModel;
  flashDeal: FlashDealModel;
}

export interface StandardCarouselModel {
  title: string;
  includeShopAllButton: boolean;
  shopAllButtonColor: string;
  products: Product[];
  productListingUrl: string;
}

export interface CardCarouselModel {
  title: string;
  imageUrl: string;
  imageAltText: string;
  includeShopAllButton: boolean;
  shopAllButtonColor: string;
  products: Product[];
  productListingUrl: string;
}

export interface NavBarModel {
  backgroundColor: string;
  textColor: string;
  navItems: NavBarItem[];
  enableCustomNavigationLinks: boolean;
}

export interface NavBarItem {
  title: string;
  url: string;
  imageUrl?: string;
  imageAltText?: string;
}

export interface ImageLeftCarouselModel {
  backgroundColor: string;
  imageUrl: string;
  imageAltText: string;
  tabletImageUrl: string;
  tabletImageAltText: string;
  products: Product[];
  productListingUrl: string;
}

export enum TargetPageType {
  EmbeddedItems = 1,
  RelativeUrl = 2,
  NoTargetPage = 3,
}

export interface WideImageModel {
  imageUrl: string;
  imageAltText: string;
  targetPageType?: TargetPageType;
  targetUrl: string;
  useRelativeUrlForTargetPage?: boolean;
}

export interface EventHeroModel {
  titleImageUrl: string;
  titleImageAltText: string;
  backgroundImageUrl: string;
  backgroundImageAltText: string;
  subtextTextColor: string;
  subtextText: string;
  countdownCta?: string;
  couponCode: string;
  couponCodeTextColor: string;
}

export interface FlashDealModel {
  textColor: string;
  backgroundColor: string;
  startTime: string;
  frequency: number;
  imageUrl: string;
  imageAltText: string;
  items: FlashDealItem[];
}

export interface FlashDealItem {
  product: Product;
  salePrice: number;
  startDateTimeUtc: string;
  endDateTimeUtc: string;
}

export interface HeroModel {
  backgroundColor: string;
  backgroundImageUrl?: string;
  useImageForBackground: boolean;
  alignmentType: AlignmentType;
  logoUrl: string;
  logoAltText: string;
  headerTextColor: string;
  bodyTextColor: string;
  header: string;
  bodyText: string;
  buttonUrl: string;
  buttonUrlType: ButtonUrlType;
  buttonText: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  hasImage: boolean;
  imageUrl?: string;
  imageAltText?: string;
  hasBottomPadding: boolean;
  hasTopPadding: boolean;
  video?: Video;
  hasButton: boolean;
}

export interface ContentModel {
  hasBottomPadding: boolean;
  hasContainer: boolean;
  hasDropShadow?: boolean;
  hasOutline?: boolean;
  hasOverlap: boolean;
  hasTopPadding: boolean;
  containerColor?: string;
  containerOutlineColor?: string;
  backgroundColor: string;
  useImageForBackground: boolean;
  backgroundImageUrl: string;
  alignmentType: AlignmentType;
  headerTextColor: string;
  bodyTextColor: string;
  header: string;
  bodyText: string;
  hasButton: boolean;
  buttonUrl: string;
  buttonUrlType: ButtonUrlType;
  buttonText: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  hasImageOrVideo: boolean;
  imageUrl?: string;
  imageAltText?: string;
  video?: Video;
  hasCaption?: boolean;
  caption?: string;
  captionTextColor?: string;
  hasListItems: boolean;
  bulletPointStyle?: BulletPointType;
  bulletPointColor?: string;
  listItems: {
    header: string;
    bodyText: string;
    listPosition: number;
  }[];
  imageAlignment: AlignmentType;
  imageWidth: ImageWidthType;
  overlapType?: OverlapType;
}

export interface FeatureModel {
  backgroundColor: string;
  bodyText: string;
  bodyTextColor: string;
  containerColor: string;
  featuresPerRow: number;
  hasBottomPadding: boolean;
  hasDropshadow: boolean;
  hasOutline: boolean;
  hasOverlap: boolean;
  hasTopPadding: boolean;
  header: string;
  headerTextColor: string;
  imageStyle: FeatureImageStyle;
  items: FeatureItemModel[];
  linkStyle: LinkStyle;
  outlineColor: string;
  overlapType?: OverlapType;
}

export interface FeatureItemModel {
  imageUrl: string;
  imageAltText: string;
  textColor: string;
  header: string;
  bodyText: string;
  buttonUrlType: ButtonUrlType;
  buttonUrl: string;
  buttonText?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
}

export interface FormModel {
  backgroundColor?: string;
  backgroundImageUrl?: string;
  bodyText: string;
  bodyTextColor: string;
  buttonBackgroundColor: string;
  buttonText: string;
  buttonTextColor: string;
  caption: string;
  captionTextColor: string;
  containerColor?: string;
  containerOutlineColor?: string;
  emailBodyText: string;
  emailSubject: string;
  emailTo: string;
  formColumnCount: number;
  formFields: FormFieldModel[];
  formTextColor: string;
  hasBottomPadding: boolean;
  hasDropShadow: boolean;
  hasOutline: boolean;
  hasOverlap: boolean;
  hasTopPadding: boolean;
  header: string;
  headerTextColor: string;
  imageUrl: string;
  imageAlignment: AlignmentType;
  imageAltText: string;
  overlapType?: OverlapType;
  useImageForBackground: boolean;
  video?: Video;
}

export interface FormFieldModel {
  formFieldId: number;
  fieldType: FormFieldInputType;
  label: string;
  isRequired: boolean;
  options: string[];
  description: string;
  sortOrder: number;
}

export interface ImageVideoModel {
  imageAlignment: AlignmentType;
  hasImageOrVideo: boolean;
  imageUrl?: string;
  imageAltText?: string;
  video?: Video;
  hasCaption?: boolean;
  captionTextColor?: string;
  caption?: string;
  layoutStyles?: string;
  content?: ReactNode;
  hasDropShadow?: boolean;
  imageVideoWidthClass?: string;
}

export enum ButtonUrlType {
  section = 1,
  internalUrl = 2,
  externalUrl = 3,
}

export enum AlignmentType {
  Center = 1,
  Left = 2,
  Right = 3,
}

export enum BulletPointType {
  RoundCheck = 1,
  SquareCheck = 2,
  DefaultCheck = 3,
  RoundX = 4,
}

export enum PageCreatorSectionType {
  None = 0,
  TwoWideImage = 1,
  FourWideImage = 2,
  ImageLeftCarousel = 3,
  StandardCarousel = 4,
  CardCarousel = 5,
  NavBar = 6,
  EventHero = 7,
  Hero = 8,
  Feature = 9,
  Content = 10,
  Form = 11,
  OneWideImage = 12,
  FlashDeal = 13,
}

export enum LinkStyle {
  noLink = 1,
  text = 2,
  button = 3,
}

export enum FeatureImageStyle {
  image = 1,
  icon = 2,
}

export enum FormFieldInputType {
  Custom = 1,
  EmailAddress = 2,
  PhoneNumber = 3,
  Address = 4,
  Dropdown = 5,
  Textarea = 6,
  TermsAndCondition = 7,
  CheckboxGroup = 8,
}

export enum ImageWidthType {
  Half = 1,
  OneThird = 2,
  TwoThirds = 3,
}

export enum LeadGenStyle {
  FeatureRow = 12,
  FeatureGrid = 13,
  FeatureContainer = 14,
}

export enum OverlapType {
  OneThird = 1,
  OneHalf = 2,
  TwoThirds = 3,
}
